import { useState, useEffect } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Link as ReactRouterLink, useLocation } from 'react-router-dom';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

import { onError } from '../../../utils/error.util';
import { Document } from '../../../models/document.model';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import { deleteDocument, getDocuments, updateDocument } from '../../../services/documents.service';
import { CircularProgressComponent } from '../../../components/admin/circular-progress.component';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

const LIMIT = 100;

function DocumentTable({ data, handleShowDeleteModal, onReorder, isUpdatingOrder }: any) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px of movement before activating
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    if (isUpdatingOrder) return; // Prevent drag end if update is in progress

    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = data.findIndex((item: Document) => item.id === active.id);
      const newIndex = data.findIndex((item: Document) => item.id === over.id);
      onReorder(oldIndex, newIndex);
    }
  };

  return (
    <>
      <TableContainer component={Paper}>
        <DndContext
          sensors={isUpdatingOrder ? [] : sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="50px"></TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Preview</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <SortableContext items={data.map((item: Document) => item.id)} strategy={verticalListSortingStrategy}>
                {data.map((row: any) => (
                  <DocumentTableRow
                    key={row.id}
                    row={row}
                    handleShowDeleteModal={handleShowDeleteModal}
                    isUpdatingOrder={isUpdatingOrder}
                  />
                ))}
              </SortableContext>
            </TableBody>
          </Table>
        </DndContext>
      </TableContainer>
    </>
  );
}

function DocumentTableRow({
  row,
  handleShowDeleteModal,
  isUpdatingOrder,
}: {
  row: Document;
  handleShowDeleteModal: any;
  isUpdatingOrder?: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.id,
    disabled: isUpdatingOrder,
  });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
    opacity: isDragging ? 0.5 : isUpdatingOrder ? 0.6 : 1,
  };

  const truncateHtml = (html: string, maxLength: number = 100) => {
    const text = html.replace(/<[^>]*>/g, '');
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  return (
    <TableRow ref={setNodeRef} style={style}>
      <TableCell>
        <Box
          {...(isUpdatingOrder ? {} : attributes)}
          {...(isUpdatingOrder ? {} : listeners)}
          sx={{
            cursor: isUpdatingOrder ? 'not-allowed' : isDragging ? 'grabbing' : 'grab',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '8px',
            opacity: isUpdatingOrder ? 0.5 : 1,
          }}
        >
          <DragIndicatorIcon color="action" fontSize="small" />
        </Box>
      </TableCell>
      <TableCell>
        <ReactRouterLink key={row.id} to={`/documents/${row.id}`}>
          <span>{row.title}</span>
        </ReactRouterLink>
      </TableCell>
      <TableCell>{truncateHtml(row.htmlContent)}</TableCell>
      <TableCell align="left">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Tooltip title="Delete">
            <ReactRouterLink onClick={() => handleShowDeleteModal(row.id)} to="">
              <DeleteIcon color="error" fontSize="small" />
            </ReactRouterLink>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );
}

export default function DocumentsContainer() {
  const location = useLocation();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdatingOrder, setIsUpdatingOrder] = useState(false);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<string | undefined>(undefined);

  useEffect(() => {
    loadDocuments();
  }, [location]);

  async function loadDocuments() {
    try {
      setIsLoading(true);
      const result = await getDocuments({ limit: LIMIT });
      // Sort by order if available, otherwise maintain current order
      const sortedItems = [...result.items].sort((a, b) => {
        const orderA = a.order ?? 999999;
        const orderB = b.order ?? 999999;
        return orderA - orderB;
      });
      setDocuments(sortedItems);
    } catch (e: any) {
      onError(e);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleReorder(oldIndex: number, newIndex: number) {
    if (isUpdatingOrder) return; // Prevent reordering while update is in progress

    const reorderedDocuments = arrayMove(documents, oldIndex, newIndex);
    setDocuments(reorderedDocuments);

    // Update order for all items
    try {
      setIsUpdatingOrder(true);
      const updatePromises = reorderedDocuments.map((item, index) => {
        const updatedItem = { ...item, order: index };
        return updateDocument(updatedItem);
      });
      await Promise.all(updatePromises);
    } catch (e: any) {
      onError(e);
      // Reload on error to restore original order
      loadDocuments();
    } finally {
      setIsUpdatingOrder(false);
    }
  }

  function handleShowDeleteModal(documentId: string) {
    setDocumentToDelete(documentId);
    setIsDeleteModalOpen(true);
  }

  function handleCloseDeleteModal() {
    setIsDeleteModalOpen(false);
    setDocumentToDelete(undefined);
  }

  async function handleDelete() {
    if (!documentToDelete) return;

    try {
      await deleteDocument(documentToDelete);
      handleCloseDeleteModal();
      loadDocuments();
    } catch (e: any) {
      onError(e);
    }
  }

  return (
    <>
      <main id="main">
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <h1>Manage Documents</h1>
              <ReactRouterLink to="/documents/new">
                <Button variant="contained" color="primary">
                  Add New Document
                </Button>
              </ReactRouterLink>
            </Box>
          </Grid>

          <Grid size={{ xs: 12 }}>
            {isLoading ? (
              <CircularProgressComponent />
            ) : (
              <DocumentTable
                data={documents}
                handleShowDeleteModal={handleShowDeleteModal}
                onReorder={handleReorder}
                isUpdatingOrder={isUpdatingOrder}
              />
            )}
          </Grid>
        </Grid>
      </main>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this document? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteModal}>Cancel</Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

